import React from 'react';

// Feature Data Interface
interface Feature {
  index: number;
  heading: string;
  subheading: string;
  previewComponent: React.ReactNode;
}

// Preview Components
const LandingPagePreview = () => (
    <div className="relative rounded-xl shadow-2xl overflow-hidden">
      <img
        src="/images/feature1.webp"
        alt="Dualite feature preview"
        className="w-full h-auto block"
        loading="lazy"
      />
    </div>
);

const AIAgentPreview = () => (
  <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-blue-600/20 to-cyan-600/20 p-4 backdrop-blur-sm border border-white/10">
    <div className="bg-[#1a1b1e] rounded-lg shadow-2xl overflow-hidden p-6">
      <div className="space-y-4">
        {/* Mock conversation interface */}
        <div className="bg-[#2a2b2e] rounded-lg p-4 border border-white/5">
          <div className="flex gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0"></div>
            <div className="flex-1">
              <p className="text-white/90 text-sm mb-1 font-medium">Your Customer</p>
              <p className="text-white/50 text-xs">Hi! I'm having trouble resetting my password. Can you help?</p>
            </div>
          </div>
        </div>
        <div className="bg-[#2a2b2e] rounded-lg p-4 border border-white/5">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex-shrink-0"></div>
            <div className="flex-1">
              <p className="text-white/90 text-sm mb-2 font-medium">Your AI Customer Support Agent</p>
              <p className="text-white/70 text-xs leading-relaxed">
                Of course! I'd be happy to help you reset your password.
                <br />
                I've just sent a password reset link to your email. Please check your inbox and spam folder.
               
                Let me know if you need any further assistance!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const DashboardPreview = () => (
    <div className="relative bg-white rounded-lg shadow-2xl overflow-hidden">
      {/* Mock dashboard */}
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-[#10110C] text-base font-semibold">Your Sales Dashboard</h4>
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500"></div>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-gray-50 rounded-lg p-2.5">
            <p className="text-[#10110C] text-lg font-bold mb-0.5">$95,241</p>
            <p className="text-[#10110C]/60 text-xs">Total Sales</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5">
            <p className="text-[#10110C] text-lg font-bold mb-0.5">2,381</p>
            <p className="text-[#10110C]/60 text-xs">Total Orders</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5">
            <p className="text-[#10110C] text-lg font-bold mb-0.5">3.2%</p>
            <p className="text-[#10110C]/60 text-xs">Conversion Rate</p>
          </div>
        </div>

        {/* Chart Area */}
        <div className="bg-gray-50 rounded-lg p-3 h-24 flex items-end justify-between gap-1">
          {[40, 65, 45, 70, 55, 80, 60, 75, 50, 85, 70, 90].map((height, i) => (
            <div 
              key={i} 
              className="flex-1 bg-gradient-to-t from-indigo-500 to-purple-500 rounded-t"
              style={{ height: `${height}%` }}
            />
          ))}
        </div>

        {/* Table Preview */}
        <div className="mt-4">
          <h5 className="text-[#10110C] text-xs font-semibold mb-2">Recent Sales</h5>
          <div className="space-y-1.5">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400"></div>
                  <div>
                    <p className="text-[#10110C] text-xs font-medium">Order #{2380 + i}</p>
                    <p className="text-[#10110C]/50 text-xs">Customer Name</p>
                  </div>
                </div>
                <p className="text-[#10110C] text-xs font-semibold">$299.00</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
);

// Features Data
const featuresData: Feature[] = [
  {
    index: 0,
    heading: "Build apps without any limits",
    subheading: "Bring your ideas to life as web and mobile apps hassle-free with our unlimited messages plan",
    previewComponent: <LandingPagePreview />
  },
  {
    index: 1,
    heading: "Build secure AI agents",
    subheading: "Create and publish smart and secure AI apps customized to your needs with adaptable prompts.",
    previewComponent: <AIAgentPreview />
  },
  {
    index: 2,
    heading: "Build literally anything you want",
    subheading: "From internal tools, games, dashboard, inventory management, invoicing systems, erp systems, and more.",
    previewComponent: <DashboardPreview />
  }
];

// Main Component
export default function FeatureShowcase() {
  return (
    <>
      {/* Section Header */}
      <div id="features" className="w-full bg-[#10110C] px-4 pt-16 sm:px-6 sm:pt-20 lg:px-8 lg:pt-24 scroll-mt-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <h2 
              className="text-white text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 group inline-block relative"
              style={{ fontFamily: 'Inter Display' }}
            >
              What can you build with Dualite?
              {/* <a 
                href="#features"
                className="absolute -left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-white/40 hover:text-white/60 no-underline"
                aria-label="Link to this section"
              >
                #
              </a> */}
            </h2>
            <p 
              className="text-white/60 text-base sm:text-lg lg:text-xl max-w-3xl mx-auto"
              style={{ fontFamily: 'Inter Display' }}
            >
              Turn your ideas into a real product in just a few prompts without any coding.
              </p>
          </div>
        </div>
      </div>

      {/* Feature Sections */}
      {featuresData.map((feature) => {
        const bgClass = feature.index === 1 ? 'bg-[#0a0b08]' : 'bg-[#10110C]';
        const isReversed = feature.index % 2 === 0;

        return (
          <div 
            key={feature.index} 
            className={`w-full ${bgClass} px-4 py-4 sm:px-6 sm:py-8 lg:px-8 lg:py-12`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                {/* Text Content */}
                <div className={isReversed ? 'order-2 lg:order-1' : 'order-2'}>
                  <h3 
                    className="text-white text-xl sm:text-2xl lg:text-3xl font-semibold mb-6"
                    style={{ fontFamily: 'Inter Display' }}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: feature.heading.replace(
                          "literally",
                          '<span class="italic font-thin">literally</span>'
                        )
                      }}
                    />
                  </h3>
                  <p 
                    className="text-white/70 text-base sm:text-lg lg:text-xl leading-relaxed"
                    style={{ fontFamily: 'Inter Display' }}
                    dangerouslySetInnerHTML={{
                      __html: feature.subheading.replace(
                        'unlimited messages plan',
                        '<a href="#pricing" class="text-white font-medium underline cursor-pointer">unlimited messages plan</a>'
                      )
                    }}
                  />
                </div>

                {/* Preview Component */}
                <div className={isReversed ? 'order-1 lg:order-2' : 'order-1'}>
                  {feature.previewComponent}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

